import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  ContentBox,
  ContentPage,
  DatePicker,
  DrawerFilterPanel,
  DropdownAction,
  FullTable,
  NoResults,
  Select,
  TableFilters,
  Text,
} from "@ficatranquilo/contabilidade.com-ui";
import useService from "../../../../hooks/useService";
import useListFilters from "../../../../hooks/useListFilters";
import MarkPaidModal from "../../../../components/MarkPaidModal";
import ApproveInvoiceModal from "../../../../components/ApproveInvoiceModal";
import RejectInvoiceModal from "../../../../components/RejectInvoiceModal";
import { scheduleHasValue } from "../../../../helpers";
import { downloadTaxInvoice } from "@helpers/taxInvoiceFile";
import { hasPermission } from "@helpers/permissions";
import { canOpenScreen } from "@helpers/companyScreens";
import { escapeHtml, formatBRL, formatCompetencia, formatDate, usePageHeader } from "@components/ow";
import {
  INVOICE_STATUS_DATA,
  InvoiceDrawer,
  InvoiceFileLink,
  InvoiceStatusCell,
  competenceSchedule,
  invoiceCompetenceOf,
  useInvoiceDrawer,
} from "../../Common/TaxInvoices";

// Página da API por requisição: uma das opções do seletor da FullTable (10, 25, 50, 100)
const PAGE_SIZE = 25;

const INITIAL_FILTERS = { status: INVOICE_STATUS_DATA[0] };

const BREADCRUMB = [{ page: "Notas fiscais", route: "/c/nfes" }];

export default function NFes() {
  const navigate = useNavigate();
  const taxInvoicesService = useService("taxInvoices");
  const paymentScheduleService = useService("payment-schedules");
  const userService = useService("users");
  // "Ver NFs" vê e baixa: aprovar, recusar e pagar seguem só ADM. O filtro de prestador
  // (que usa a lista de usuários) aparece com "Ver colaboradores"; o "Ver pagamento",
  // com a tela de pagamentos.
  const auth = JSON.parse(localStorage.getItem("user_ow") || "{}");
  const isAdmin = auth?.role === "ADM";
  const canFilterUsers = isAdmin || hasPermission("users:read");
  const canOpenPayments = canOpenScreen("paymentSchedules", auth);

  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: PAGE_SIZE });
  const [paginationData, setPaginationData] = useState({
    totalRegisters: 0,
    list: [],
    totalPages: 1,
  });
  const [loading, setLoading] = useState(true);
  const [userOptions, setUserOptions] = useState([]);
  const [invoiceToApprove, setInvoiceToApprove] = useState(null);
  const [invoiceToReject, setInvoiceToReject] = useState(null);
  const [scheduleToPay, setScheduleToPay] = useState(null);
  // Só a resposta da última requisição vale (filtros seguidos podem voltar fora de ordem)
  const lastRequest = useRef(0);
  // Drawer da nota e o cronograma dela ("Ver pagamento")
  const drawer = useInvoiceDrawer({ enabled: canOpenPayments, fetchSchedule: (nf) => fetchScheduleOf(nf) });

  const {
    storeFilters,
    filters,
    filter,
    openFilter,
    setOpenFilter,
    handleSetFilters,
    handleConfirmFilter,
    handleClearFilters,
    hasFilter,
  } = useListFilters(INITIAL_FILTERS, setPagination);

  usePageHeader({ title: "Notas fiscais", breadcrumb: BREADCRUMB });

  const statusValue = filters.status?.value || "";
  const searchTerm = (filters.search || "").trim();
  const competence = filters.competence || null;
  const competenceKey = competence ? `${competence.getFullYear()}-${competence.getMonth() + 1}` : "";
  const usersValue = filters.users || "";

  useEffect(() => {
    fetchInvoices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.pageIndex, pagination.pageSize, statusValue, searchTerm, competenceKey, usersValue]);

  useEffect(() => {
    if (canFilterUsers) fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function fetchUsers() {
    try {
      const result = await userService.list("role=EMP&limit=9999&order=name:ASC");
      const list = result?.data?.list || [];
      setUserOptions(list.map((u) => ({ value: u.id, label: u.name || u.email })));
    } catch (error) {
      // Sem a lista, o filtro de prestador só não aparece
    }
  }

  function buildQuery() {
    // Visão da empresa: sem scope=company o back devolve só as NFs de quem chama
    const params = [`scope=company`, `page=${pagination.pageIndex + 1}`, `limit=${pagination.pageSize}`];
    if (statusValue) params.push(`status=${statusValue}`);
    if (competence) params.push(`month=${competence.getMonth() + 1}`, `year=${competence.getFullYear()}`);
    if (searchTerm) params.push(`filename=${encodeURIComponent(searchTerm)}`);
    if (usersValue) params.push(`sharedUsers=${usersValue}`);
    return params.join("&");
  }

  async function fetchInvoices() {
    const request = ++lastRequest.current;
    setLoading(true);
    try {
      const result = await taxInvoicesService.list(buildQuery());
      if (request !== lastRequest.current) return;
      if (result && result.data && result.data.list) {
        setPaginationData(result.data);
      } else {
        throw new Error(typeof result === "string" ? result : "Erro ao carregar notas fiscais");
      }
    } catch (error) {
      if (request !== lastRequest.current) return;
      console.error(error);
      const errorMessage = error?.response?.data?.msg || error?.message || "Erro ao carregar notas fiscais";
      toast.error(errorMessage);
      setPaginationData({
        totalRegisters: 0,
        list: [],
        totalPages: 1,
      });
    }
    setLoading(false);
  }

  /** Cronograma da nota (nota recusada não tem: o back desfaz o vínculo). */
  async function fetchScheduleOf(nf) {
    const result = await paymentScheduleService.list(`scope=company&tax_invoice_id=${nf.id}&limit=1`);
    return result?.data?.list?.[0] || null;
  }

  // Limpa busca, competência, prestador e também o status (o "Limpar filtros" do vazio)
  function clearAll() {
    handleClearFilters();
    storeFilters.setFilters({ status: INVOICE_STATUS_DATA[0] });
  }

  async function handleMarkAsPaid(nf) {
    try {
      const schedule = await fetchScheduleOf(nf);
      if (!schedule) {
        toast.error("Não encontramos o pagamento desta nota.");
        return;
      }
      // Sem valor definido não há o que dar baixa: o back recusa
      if (!scheduleHasValue(schedule)) {
        toast.error("Este cronograma ainda não tem valor definido. Defina o valor antes de registrar o pagamento.");
        return;
      }
      setScheduleToPay(schedule);
    } catch (error) {
      toast.error("Erro ao carregar o pagamento desta nota");
    }
  }

  function afterAction() {
    drawer.close();
    fetchInvoices();
  }

  const handleDownload = (nf) => downloadTaxInvoice(taxInvoicesService, nf);

  const columns = [
    {
      id: "user",
      header: "Prestador",
      size: 220,
      maxSize: 300,
      cell: ({ row }) => (
        <div>
          <Text $text={escapeHtml(row.original.user?.name || "-")} $variant="paragraph_small_bold" $color="N900" />
          {row.original.user?.email ? (
            <Text $text={escapeHtml(row.original.user.email)} $variant="caption_medium" $color="N400" />
          ) : null}
        </div>
      ),
    },
    {
      id: "competence",
      header: "Competência",
      size: 110,
      cell: ({ row }) => formatCompetencia(invoiceCompetenceOf(row.original)),
    },
    {
      id: "file",
      header: "Arquivo",
      size: 240,
      cell: ({ row }) => <InvoiceFileLink invoice={row.original} onDownload={() => handleDownload(row.original)} />,
    },
    { id: "value", header: "Valor", size: 120, cell: ({ row }) => formatBRL(row.original.value) },
    {
      id: "status",
      header: "Status",
      size: 130,
      cell: ({ row }) => <InvoiceStatusCell invoice={row.original} />,
    },
    { id: "created_at", header: "Enviada em", size: 110, cell: ({ row }) => formatDate(row.original.created_at) },
    {
      id: "actions",
      // O "Ações" da lib tem 70px; abaixo disso a seta sai da célula
      size: 80,
      minSize: 80,
      maxSize: 80,
      align: "right",
      cell: ({ row }) => {
        const nf = row.original;
        return (
          <DropdownAction
            options={[
              { label: "Ver dados", action: () => drawer.openDrawer(nf) },
              { label: "Baixar nota", action: () => handleDownload(nf) },
              { label: "Aprovar nota", action: () => setInvoiceToApprove(nf), show: isAdmin && nf.status === "PENDING" },
              {
                label: "Marcar como pago",
                action: () => handleMarkAsPaid(nf),
                show: isAdmin && nf.status === "APPROVED",
              },
              // Uma entrada só para pendente e aprovada: o DropdownAction usa o label como key.
              // A aprovada só é recusada com o pagamento em aberto (o back barra o pago).
              {
                label: "Recusar nota",
                action: () => setInvoiceToReject(nf),
                show: isAdmin && (nf.status === "PENDING" || nf.status === "APPROVED"),
              },
            ]}
          />
        );
      },
    },
  ];

  const filtering = !!statusValue || !!hasFilter || !!searchTerm;
  const noResults = filtering ? (
    <NoResults notResultText="Nenhuma nota fiscal encontrada" includeText="Limpar filtros" includeAction={clearAll} />
  ) : (
    <NoResults notResultText="Nenhuma nota fiscal por aqui" />
  );

  return (
    <ContentPage>
      <ContentBox>
        <TableFilters
          storeFilters={storeFilters}
          searchTerm="search"
          searchPlaceholder="Buscar pelo nome do arquivo"
          statusData={INVOICE_STATUS_DATA}
          filter={filter}
          handleSetFilters={handleSetFilters}
          handleClearFilters={handleClearFilters}
          setStatePagination={setPagination}
          openFilter={() => setOpenFilter((prev) => !prev)}
          disableExport
          hasFilter={hasFilter}
        />

        <FullTable
          data={paginationData.list}
          columns={columns}
          rowCount={paginationData.totalRegisters}
          pagination={pagination}
          setPagination={setPagination}
          getRowId={(row) => String(row.id)}
          isLoading={loading}
          onRowClick={drawer.openDrawer}
          noResults={noResults}
        />
      </ContentBox>

      <DrawerFilterPanel
        $showDrawer={openFilter}
        $setShowDrawer={() => setOpenFilter((prev) => !prev)}
        $confirmFilter={handleConfirmFilter}
        $clearFilters={handleClearFilters}
        $hasFilter={hasFilter}
      >
        <DatePicker
          $idForm="nfes-competence"
          $label="Competência"
          $variant="default"
          $isCompetence
          $isClearable
          $date={filter.competence || null}
          $setDate={(date) => handleSetFilters(null, "competence", date || null)}
        />
        {canFilterUsers && userOptions.length > 0 ? (
          <Select
            isMulti
            closeMenuOnSelect={false}
            hideSelectedOptions={false}
            $idForm="nfes-users"
            $label="Prestador"
            $options={userOptions}
            placeholder="Selecione os prestadores"
            value={userOptions.filter((o) => (filter.users || "").split("|").includes(String(o.value)))}
            onChange={(options) =>
              handleSetFilters(null, "users", (options || []).map((o) => o.value).join("|"))
            }
          />
        ) : null}
      </DrawerFilterPanel>

      <InvoiceDrawer
        drawer={drawer}
        subtitle={drawer.invoice ? escapeHtml(drawer.invoice.user?.name || "-") : null}
        // Dados bancários só vêm para o ADM e para "Ver dados bancários"
        showBankData
        onDownload={handleDownload}
        onOpenPayment={(id) => navigate(`/c/payment-schedules/${id}`)}
      />

      {invoiceToApprove && (
        <ApproveInvoiceModal
          invoice={invoiceToApprove}
          schedule={competenceSchedule(invoiceToApprove)}
          onClose={() => setInvoiceToApprove(null)}
          onApproved={() => {
            setInvoiceToApprove(null);
            afterAction();
          }}
        />
      )}

      {invoiceToReject && (
        <RejectInvoiceModal
          invoice={invoiceToReject}
          schedule={competenceSchedule(invoiceToReject)}
          onClose={() => setInvoiceToReject(null)}
          onRejected={() => {
            setInvoiceToReject(null);
            afterAction();
          }}
        />
      )}

      {scheduleToPay && (
        <MarkPaidModal
          schedule={scheduleToPay}
          onClose={() => setScheduleToPay(null)}
          onPaid={() => {
            setScheduleToPay(null);
            afterAction();
          }}
        />
      )}
    </ContentPage>
  );
}
